<template>
  <div v-if="!userStore.isLogged" class="login-container-onetap">
    <div
      id="g_id_onload"
      ref="oneTapContainer"
      style="position: absolute; top: -9999px; left: -9999px"
    ></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { type GoogleCredentialResponse } from 'vue3-google-signin';
import { useUserStore } from "store/user";
import { useAuthStore } from "~/features/auth";
import { typesafeI18n } from "~/locales/i18n-vue";

const config = useRuntimeConfig()
const route = useRoute()
const toast = useToast()
const { LL } = typesafeI18n();
const userStore = useUserStore();

const GOOGLE_CLIENT_ID = config.public.googleClientId;
const API_ENDPOINT = config.public.apiBase + '/api/auth/google-onetap';
const REDIRECT_URL_SUCCESS = route.path;

const oneTapContainer = ref<HTMLElement | null>(null)
const loginAttempted = ref(false);

const sendTokenToBackend = async (token: string) => {
  try {
    const response = await $fetch<{
      status: boolean;
      message: string;
      jwt?: string;
    }>(API_ENDPOINT, {
      method: 'POST',
      body: { token },
    });

    if (response.status && response.jwt) {
      await userStore.loginWithProvider({
        jwt: response.jwt,
      });

      toast.add({
        title: LL.value[response.message](),
        color: 'success',
      });
    } else {
      throw new Error(response.message);
    }
  } catch (error) {
    toast.add({
      title: LL.value[error.message](),
      color: 'error',
    });
  }
}

const handleCredentialResponse = (response: GoogleCredentialResponse) => {
  loginAttempted.value = true;
  console.log('Encoded JWT ID token received:', response);

  if (!response || !response.credential) {
    return;
  }
  sendTokenToBackend(response.credential);
}

const initGoogleOneTap = () => {
  if (window.google?.accounts?.id && GOOGLE_CLIENT_ID) {
      try {
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleCredentialResponse,
            auto_select: false,
            cancel_on_tap_outside: true,
        });

        window.google.accounts.id.prompt((notification) => {
            console.log('Google One Tap Prompt notification:', notification);

            if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                console.warn(`One Tap UI not displayed. Reason: ${notification.getNotDisplayedReason() || 'Skipped'}`);
            }
        });
      } catch (error) {
          console.error('Google One Tap initialization error:', error);
      }
  } else {
    console.error('Google Identity Services script not loaded yet.');
  }
}

onMounted(() => {
  if (!userStore.isLogged) {
    const checkGsiLoad = () => {
      if (window.google?.accounts?.id) {
        initGoogleOneTap();
      } else {
        setTimeout(checkGsiLoad, 100);
      }
    };
    checkGsiLoad();
  }
});

useHead({
  script: [
    {
      src: 'https://accounts.google.com/gsi/client',
      async: true,
      defer: true,
    },
  ],
})
</script>

<style>
.login-container-onetap {
  padding: 1rem;
  text-align: center;
  background-color: none;
}
.mdl-js {
  border-radius: 10px;
}
</style>
